import { useMemo, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';

import { AppLayout } from '@/components/AppLayout';
import { CommentsSection } from '@/components/comments/CommentsSection';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthor } from '@/hooks/useAuthor';
import { useAppContext } from '@/hooks/useAppContext';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { useToast } from '@/hooks/useToast';
import { useCachedNostrQuery } from '@/hooks/useCachedNostrQuery';
import { genUserName } from '@/lib/genUserName';
import { cn, generateVividGradient } from '@/lib/utils';

import {
  ArrowLeft,
  GitBranch,
  Copy,
  Check,
  User,
  ExternalLink,
  Folder,
  Share2,
  Eye,
} from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { SupportButton } from '@/components/SupportButton';
import { ReactionButton } from '@/components/git/ReactionButton';
import { ShareProjectPublicDialog } from '@/components/ShareProjectPublicDialog';

import NotFound from './NotFound';

interface ProjectPageProps {
  pubkey: string;
  projectSlug: string;
}

function useProjectBySlug(pubkey: string, slug: string) {
  const { config } = useAppContext();
  const gitRelays = config.ngitServers.map((server) => `wss://${server}/`);

  const result = useCachedNostrQuery({
    filters: [
      {
        kinds: [30617],
        authors: [pubkey],
        '#d': [slug],
        limit: 1,
      },
    ],
    relays: gitRelays,
    timeout: 8000,
    cacheMaxAge: 5 * 60 * 1000, // Cache for 5 minutes
    useStaleWhileRevalidate: true,
    queryOptions: {
      enabled: !!pubkey && !!slug,
      select: (events) => events[0] ?? null,
    },
  });

  return result;
}

function ProjectPageSkeleton() {
  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto">
        <Skeleton className="h-8 w-24 mb-6" />
        <div className="rounded-2xl border border-border/60 overflow-hidden">
          <div className="aspect-[21/9] bg-muted/60 flex items-center justify-center">
            <Folder className="h-24 w-24 text-muted-foreground/30" />
          </div>
          <div className="p-6 space-y-4">
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
            <div className="flex gap-2 pt-4">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-10" />
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function ProjectPageContent({ pubkey, projectSlug }: ProjectPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const projectsManager = useProjectsManager();
  const [isCloning, setIsCloning] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const { data: project, isLoading, error } = useProjectBySlug(pubkey, projectSlug);
  const { data: authorData } = useAuthor(pubkey);

  const metadata = authorData?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  const npub = useMemo(() => nip19.npubEncode(pubkey), [pubkey]);

  // Extract project details
  const nameTag = project?.tags.find((tag) => tag[0] === 'name');
  const dTag = project?.tags.find((tag) => tag[0] === 'd');
  const descTag = project?.tags.find((tag) => tag[0] === 'description');
  const cloneTag = project?.tags.find((tag) => tag[0] === 'clone');
  const webTag = project?.tags.find((tag) => tag[0] === 'web');
  const imageTag = project?.tags.find((tag) => tag[0] === 'image');

  const projectName = nameTag?.[1] || dTag?.[1] || 'Unnamed Repository';
  const description = descTag?.[1] || '';
  const cloneUrl = cloneTag?.[1] || (dTag?.[1] ? `nostr://${pubkey}/${dTag[1]}` : '');
  const webUrl = webTag?.[1];
  const coverImage = imageTag?.[1];

  const gradientStyle = useMemo(
    () => ({
      background: project ? generateVividGradient(project.id) : 'transparent',
    }),
    [project]
  );

  const pageUrl = `https://afterhours.diy/${npub}/${projectSlug}`;
  const ogImage = coverImage || 'https://afterhours.diy/afterhours-og.png';
  const seoDescription = description || `${projectName} - A Nostr git project built with AfterHours`;

  useSeoMeta({
    title: project ? `${projectName} – AfterHours` : 'Project – AfterHours',
    description: seoDescription,
    ogTitle: `${projectName} – AfterHours`,
    ogDescription: seoDescription,
    ogImage: ogImage,
    ogUrl: pageUrl,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: `${projectName} – AfterHours`,
    twitterDescription: seoDescription,
    twitterImage: ogImage,
    link: [
      { rel: 'canonical', href: pageUrl },
    ],
  });

  // JSON-LD Structured Data
  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareSourceCode',
    name: projectName,
    description: seoDescription,
    url: pageUrl,
    image: ogImage,
    author: {
      '@type': 'Person',
      name: displayName,
      identifier: npub,
      ...(metadata?.picture && { image: metadata.picture }),
      ...(metadata?.nip05 && { email: metadata.nip05 }),
      ...(metadata?.website && { url: metadata.website }),
    },
    ...(webUrl && {
      codeRepository: webUrl,
      isAccessibleForFree: true,
    }),
    ...(cloneUrl && {
      downloadUrl: cloneUrl,
    }),
    ...(project?.created_at && {
      datePublished: new Date(project.created_at * 1000).toISOString(),
    }),
    inLanguage: 'en',
    programmingLanguage: 'JavaScript',
  }), [projectName, seoDescription, pageUrl, ogImage, displayName, npub, metadata, webUrl, cloneUrl, project?.created_at]);

  const handleClone = useCallback(async () => {
    if (!cloneUrl) return;

    setIsCloning(true);
    try {
      await projectsManager.init();
      const newProject = await projectsManager.cloneProject(projectName, cloneUrl);

      toast({
        title: 'Repository imported',
        description: `"${newProject.name}" is ready to edit.`,
      });

      navigate(`/project/${newProject.id}?build`);
    } catch (err) {
      console.error('Failed to clone repository:', err);
      toast({
        title: 'Failed to import repository',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsCloning(false);
    }
  }, [cloneUrl, navigate, projectsManager, projectName, toast]);

  const handleCopy = async () => {
    if (!cloneUrl) return;
    try {
      await navigator.clipboard.writeText(cloneUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      toast({
        title: 'Failed to copy',
        description: 'Could not copy repository URL',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return <ProjectPageSkeleton />;
  }

  if (error || !project) {
    return <NotFound />;
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6"
        >
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-muted-foreground hover:text-foreground"
            onClick={() => navigate(`/${npub}`)}
          >
            <ArrowLeft className="h-4 w-4" />
            {t('backToProfile', 'Back to profile')}
          </Button>
        </motion.div>

        {/* Project Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/60 overflow-hidden shadow-sm mb-8"
        >
          {coverImage ? (
            <div className="aspect-[21/9] relative">
              <img
                src={coverImage}
                alt={projectName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to gradient if image fails to load
                  const target = e.target as HTMLImageElement;
                  const parent = target.parentElement;
                  if (parent) {
                    parent.style.cssText = `background: ${generateVividGradient(project?.id || '')}`;
                    parent.classList.add('flex', 'items-center', 'justify-center');
                    parent.innerHTML = '<svg class="h-24 w-24 text-white/50" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" /></svg>';
                  }
                }}
              />
            </div>
          ) : (
            <div className="aspect-[21/9] flex items-center justify-center" style={gradientStyle}>
              <Folder className="h-24 w-24 text-white/50" />
            </div>
          )}

          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2 min-w-0 flex-1">
                  <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
                    {projectName}
                  </h1>
                  {description && (
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                      {description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ReactionButton repoEventId={project.id} />
                  <SupportButton pubkey={pubkey} event={project} />
                  <div className="rounded-xl bg-muted/70 p-3">
                    <GitBranch className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              </div>

              {/* Author */}
              <Link
                to={`/${npub}`}
                className="inline-flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={metadata?.picture} />
                  <AvatarFallback>
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start min-w-0">
                  <span className="font-medium truncate max-w-[15rem]">
                    {displayName}
                  </span>
                  {metadata?.nip05 && (
                    <span className="text-xs text-muted-foreground/80 truncate max-w-[15rem]">
                      {metadata.nip05}
                    </span>
                  )}
                </div>
              </Link>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="default"
                className="rounded-xl shadow-sm"
                onClick={handleClone}
                disabled={isCloning || !cloneUrl}
              >
                <GitBranch className="h-4 w-4 mr-2" />
                {isCloning ? 'Cloning...' : 'Clone Project'}
              </Button>
              {webUrl && (
                <Button
                  asChild
                  size="default"
                  className="rounded-xl shadow-sm"
                >
                  <a href={webUrl} target="_blank" rel="noreferrer">
                    <Eye className="h-4 w-4 mr-2" />
                    {t('preview', 'Preview')}
                  </a>
                </Button>
              )}
              <Button
                size="default"
                variant="outline"
                className="rounded-xl border-border/60"
                onClick={() => setShareDialogOpen(true)}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
              <Button
                size="default"
                variant="outline"
                className={cn(
                  'rounded-xl border-border/60 hover:border-primary/40 hover:bg-primary/5',
                  !copied && 'opacity-60 hover:opacity-100',
                  copied && 'border-green-500/60 bg-green-500/10 text-green-600 opacity-100'
                )}
                onClick={handleCopy}
                disabled={!cloneUrl}
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Comments Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <CommentsSection
            root={project}
            title={t('discussion', 'Discussion')}
            emptyStateMessage={t('noCommentsYet', 'No comments yet')}
            emptyStateSubtitle={t('beFirstToComment', 'Be the first to share your thoughts about this project!')}
          />
        </motion.div>
      </div>

      {/* Share Dialog */}
      <ShareProjectPublicDialog
        projectName={projectName}
        projectUrl={`https://afterhours.diy/${npub}/${projectSlug}`}
        liveUrl={webUrl}
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
      />
    </AppLayout>
  );
}

export default function ProjectPage() {
  const { npub, projectSlug } = useParams<{ npub: string; projectSlug: string }>();

  // Decode the npub to get the pubkey
  const pubkey = useMemo(() => {
    if (!npub) return null;
    try {
      const decoded = nip19.decode(npub);
      if (decoded.type === 'npub') {
        return decoded.data;
      }
      if (decoded.type === 'nprofile') {
        return decoded.data.pubkey;
      }
      return null;
    } catch {
      return null;
    }
  }, [npub]);

  if (!pubkey || !projectSlug) {
    return <NotFound />;
  }

  return <ProjectPageContent pubkey={pubkey} projectSlug={projectSlug} />;
}
